"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "@/components/ui/select";
import { format, parse, parseISO } from "date-fns";
import { API } from "@/App";
import api from "@/api/axios";
import { useProjects } from "@/hooks/Projects";

export default function TimeEntryPopup({
                                           open,
                                           onClose,
                                           form, setForm,
                                           userId,
                                           fetchMonthlyData,
                                           isTimeSlotValid,
                                           user
                                       }) {
    const projects = useProjects();
    const [tasks, setTasks] = useState([]);

    const fetchUserTasks = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await api.get(`${API}/tasks`, {
                params: { assignee_id: userId },
            });
            setTasks(res.data || []);
        } catch (err) {
            console.error("Failed to fetch tasks:", err);
        }
    }, [userId]);



    useEffect(() => {
        console.log(form, "form for timesheet forcedas")
    }, [form])


    useEffect(() => {
        fetchUserTasks();
    }, [fetchUserTasks, userId]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const filteredTasks = tasks.filter(
        (task) => task.project_id === form.project_id
    );

    const submitEntry = async (e) => {
        e.preventDefault();
        if (!userId || !form.project_id) {
            alert("Please select a project.");
            return;
        }

        const validationResult = isTimeSlotValid(
            form.start_at,
            form.end_at,
            form.date
        );
        if (!validationResult.isValid) {
            alert(validationResult.message);
            return;
        }

        const sISO = format(parse(form.start_at, 'HH:mm', parseISO(form.date)), "yyyy-MM-dd'T'HH:mm:ss.SSS");
        const eISO = format(parse(form.end_at, 'HH:mm', parseISO(form.date)), "yyyy-MM-dd'T'HH:mm:ss.SSS");


        if (new Date(eISO) <= new Date(sISO)) {
            alert("End time must be after start time.");
            return;
        }

        try {
            if (form._id) {
                // UPDATE logic
                await api.put(`${API}/time-entries/${form._id}`, {
                    user_id: userId,
                    project_id: form.project_id,
                    task_id: form.task_id,
                    date: form.date,
                    start_at: sISO,
                    end_at: eISO,
                    break_minutes: Number(form.break_minutes || 0),
                    billable: form.billable,
                    location: form.location,
                    notes: form.notes,
                });
            } else {
                // CREATE logic
                await api.post(`${API}/time-entries`, {
                    user_id: userId,
                    project_id: form.project_id,
                    task_id: form.task_id,
                    date: form.date,
                    start_at: sISO,
                    end_at: eISO,
                    break_minutes: Number(form.break_minutes || 0),
                    billable: form.billable,
                    location: form.location,
                    notes: form.notes,
                });
            }
            await fetchMonthlyData();
            onClose();
        } catch (ex) {
            alert(ex.response?.data?.detail || ex.response?.data?.message || "Failed to save entry.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] bg-white text-gray-900">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">
                        {form._id ? "Edit Time Entry" : "Add Time Entry"} - {user?.name}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={submitEntry} className="space-y-4">
                    {/* Project Selector */}
                    <div>
                        <Label>Project</Label>
                        <Select
                            value={form.project_id || ""}
                            onValueChange={(value) => handleChange("project_id", value)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select a project" />
                            </SelectTrigger>
                            <SelectContent>
                                {projects?.map((project) => (
                                    <SelectItem key={project._id} value={project._id}>
                                        {project.name} - {project?.current_stage_idx}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Task Selector */}
                    <div>
                        <Label>Task</Label>
                        <Select
                            value={form.task_id || ""}
                            onValueChange={(value) => handleChange("task_id", value)}
                            disabled={!filteredTasks.length}
                        >
                            <SelectTrigger>
                                <SelectValue
                                    placeholder={
                                        filteredTasks.length
                                            ? "Select a task"
                                            : "No tasks available"
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {filteredTasks.map((task) => (
                                    <SelectItem key={task.id} value={task.id}>
                                        {task.name || `Task #${task.id}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Time Inputs */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Start Time</Label>
                            <Input
                                type="time"
                                value={form.start_at || ""}
                                onChange={(e) => handleChange("start_at", e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>End Time</Label>
                            <Input
                                type="time"
                                value={form.end_at || ""}
                                onChange={(e) => handleChange("end_at", e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Date */}
                    <div>
                        <Label>Date - {form.date}</Label>
                    </div>

                    {/* Break */}
                    <div>
                        <Label>Break (minutes)</Label>
                        <Input
                            type="number"
                            min="0"
                            value={form.break_minutes || ""}
                            onChange={(e) => handleChange("break_minutes", e.target.value)}
                        />
                    </div>

                    {/* Location */}
                    <div>
                        <Label>Location</Label>
                        <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="office">Office</SelectItem><SelectItem value="site">Site</SelectItem><SelectItem value="remote">Remote</SelectItem></SelectContent>
                        </Select>
                    </div>

                    {/* Notes */}
                    <div>
                        <Label>Notes</Label>
                        <Input
                            value={form.notes || ""}
                            onChange={(e) => handleChange("notes", e.target.value)}
                            placeholder="Optional notes"
                        />
                    </div>

                    {/* Footer */}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit">Submit</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
